/**
 * Triggert einen subtilen Konfetti-Burst genau einmal pro Schlüssel
 * (z.B. pro Workshop-ID). Wahl wird in localStorage persistiert — nach
 * dem ersten Anzeigen rastet die Reveal-Karte still ein, ohne dem User
 * jedes Mal Konfetti vor die Nase zu werfen.
 *
 * Respektiert `prefers-reduced-motion`: in dem Fall überspringen wir das
 * Konfetti ganz.
 */
import confetti from "canvas-confetti";
import { useEffect, useRef } from "react";

const STORAGE_PREFIX = "strategify_confetti_seen:";

export function useConfettiOnce(key: string | null, enabled: boolean): void {
  const fired = useRef(false);

  useEffect(() => {
    if (!enabled || !key || fired.current) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(STORAGE_PREFIX + key);
    } catch {
      /* localStorage blockiert (Privacy-Mode) — wir feiern trotzdem */
    }
    if (stored === "1") return;

    fired.current = true;
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, "1");
    } catch {
      /* siehe oben */
    }

    // Drei Bursts aus drei Richtungen — wirkt voluminöser ohne kindlich
    // zu sein. Farben: Türkis-Brand + warmes Gold (Accent).
    const colors = ["#1f7373", "#3ba8a8", "#fbbf24", "#ffffff"];
    void confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.3, x: 0.5 },
      colors,
      ticks: 200,
    });
    setTimeout(() => {
      void confetti({
        particleCount: 40,
        spread: 60,
        angle: 60,
        origin: { y: 0.4, x: 0 },
        colors,
      });
    }, 180);
    setTimeout(() => {
      void confetti({
        particleCount: 40,
        spread: 60,
        angle: 120,
        origin: { y: 0.4, x: 1 },
        colors,
      });
    }, 360);
  }, [key, enabled]);
}
